"use client";

import { useCallback, useEffect, useRef } from "react";

function shuffle<T>(arr: T[]): T[] {
	const a = [...arr];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

function slotDuration(i: number, total: number, maxMs = 400): number {
	const t = i / Math.max(total - 1, 1);
	return 33 + (maxMs - 33) * t ** 1.6;
}

function sleep(ms: number): Promise<void> {
	return new Promise((r) => setTimeout(r, ms));
}

export function useSlotMachine(imageUrls: string[], columns: number) {
	const gridRef = useRef<HTMLDivElement>(null);
	const cellBusyRef = useRef<boolean[]>([]);
	const cellImgsRef = useRef<HTMLImageElement[]>([]);
	const reservedRef = useRef<Map<number, string>>(new Map());
	const builtRef = useRef(false);

	const flickerCount = Math.round(imageUrls.length * 0.55);

	const pickHero = useCallback(
		(idx: number) => {
			const reserved = reservedRef.current;
			const usedSet = new Set(reserved.values());
			usedSet.delete(reserved.get(idx) ?? "");
			const available = imageUrls.filter((url) => !usedSet.has(url));
			const pool = available.length > 0 ? available : imageUrls;
			const hero = pool[Math.floor(Math.random() * pool.length)];
			reserved.set(idx, hero);
			return hero;
		},
		[imageUrls],
	);

	const spinCell = useCallback(
		async (idx: number, blankMs = 0) => {
			const busy = cellBusyRef.current;
			const imgs = cellImgsRef.current;
			if (busy[idx] || !imgs[idx]) return;
			busy[idx] = true;

			// Reserve hero synchronously before any async work
			const hero = pickHero(idx);

			const img = imgs[idx];

			img.style.opacity = "0";
			if (blankMs > 0) await sleep(blankMs);

			const flickerSet = shuffle([...imageUrls]).slice(0, flickerCount);

			for (let i = 0; i < flickerSet.length; i++) {
				img.src = flickerSet[i];
				img.style.opacity = "1";
				await sleep(slotDuration(i, flickerSet.length));
			}

			img.src = hero;
			img.style.opacity = "1";
			await sleep(5000);

			busy[idx] = false;
		},
		[imageUrls, flickerCount, pickHero],
	);

	useEffect(() => {
		const grid = gridRef.current;
		if (!grid || imageUrls.length === 0 || builtRef.current) return;
		builtRef.current = true;

		// Preload images
		for (const src of imageUrls) {
			const preload = new Image();
			preload.src = src;
		}

		// Generate enough cells for complete rows at all breakpoints
		// CSS grid-template-rows + grid-auto-rows:0 controls visible rows
		const totalCells = 70;
		const busy: boolean[] = [];
		const imgs: HTMLImageElement[] = [];

		for (let i = 0; i < totalCells; i++) {
			const cell = document.createElement("div");
			cell.dataset.cell = "";
			const img = document.createElement("img");
			img.alt = "";
			img.style.opacity = "0";
			cell.appendChild(img);
			grid.appendChild(cell);
			imgs.push(img);
			busy.push(false);

			const idx = i;
			cell.addEventListener("click", () => spinCell(idx));
		}

		cellBusyRef.current = busy;
		cellImgsRef.current = imgs;

		// Stagger start: delays 300ms–3500ms, shuffled
		const delays = Array.from({ length: totalCells }, (_, i) =>
			300 + Math.round((i / Math.max(totalCells - 1, 1)) * 3200),
		);
		const shuffledDelays = shuffle(delays);
		for (let i = 0; i < totalCells; i++) {
			spinCell(i, shuffledDelays[i]);
		}

		return () => {
			// Cleanup: remove generated cells
			while (grid.firstChild) {
				grid.removeChild(grid.firstChild);
			}
			builtRef.current = false;
			cellBusyRef.current = [];
			cellImgsRef.current = [];
			reservedRef.current = new Map();
		};
	}, [imageUrls, spinCell]);

	return gridRef;
}
